var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React from 'react';
import ReactDOM from 'react-dom';
import { GlueDefaultRouteObject } from './routing';
import { EventManager, mergeGlueRouteObjectArrays } from './utils';
import { createBrowserHistory } from 'history';
import { Api } from './api/Api';
import Smartfire, { defineBlock } from '@smartfire/gutenberg-react';
import { triggerGlueReady } from './glue-on-ready';
export const BlockApi = Smartfire;
// import type {SmartBlockSmartPropsDefinition, SmartProps} from "@smartfire/gutenberg-react"
// import type {DefineBlockProp} from "@smartfire/gutenberg-react/lib/defineBlock";
export var GlueEvents;
(function (GlueEvents) {
    /**
     * Authentication module has been setup and can be used.
     */
    GlueEvents["AUTHENTICATION_SETUP"] = "[authentication] setup";
    /**
     * A specific rendering is requested NOW (e.g. login UI)
     */
    GlueEvents["ROUTING_OVERRIDE"] = "[routing] override current route";
    GlueEvents["ROUTING_REFRESH"] = "[routing] refresh";
    GlueEvents["COMPONENTS_REFRESH"] = "[components] refresh";
    GlueEvents["SETTING_REFRESH"] = "[setting] refresh";
    // Base string for addFilter event
    GlueEvents["ADD_FILTER_PREFIX"] = "[filters]";
})(GlueEvents || (GlueEvents = {}));
function getPriorityLevel(a) {
    return a.priority === undefined ? Infinity : a.priority;
}
export class GlueAPI {
    constructor(host = '/') {
        this.host = host;
        this.eventsManager = new EventManager();
        this._features = {
            frontTranslation: false,
            editingTranslation: false,
            smartForm: false,
            multiWebsite: false,
        };
        this._frontLanguages = ['fr'];
        this._editingLanguages = ['fr'];
        this._components = [];
        this.filters = {};
        this._websites = [{ name: 'Default website', code: 'default', bodyClass: 'default' }];
        this._layouts = [];
        this._routes = GlueDefaultRouteObject;
        this._settings = [];
        this._browserHistory = createBrowserHistory();
    }
    get authentication() {
        return this._authentication;
    }
    set authentication(value) {
        this._authentication = value;
        this.eventsManager.fire(GlueEvents.AUTHENTICATION_SETUP, value);
    }
    get websites() {
        return this._websites;
    }
    get layouts() {
        return this._layouts;
    }
    get routes() {
        return this._routes;
    }
    get settings() {
        return this._settings;
    }
    get browserHistory() {
        return this._browserHistory;
    }
    get api() {
        if (!this._api) {
            this._api = new Api(this);
        }
        return this._api;
    }
    get frontAvailableLanguages() {
        return this._frontLanguages;
    }
    get editingAvailableLanguages() {
        return this._editingLanguages;
    }
    isSmartFormEnabled() {
        return this._features.smartForm;
    }
    isFrontTranslationEnabled() {
        return this._features.frontTranslation;
    }
    isFrontEditingEnabled() {
        return this._features.editingTranslation;
    }
    isMultiWebsite() {
        return this._features.multiWebsite;
    }
    websiteFromCode(code) {
        return this._websites.find((e) => e.code === code);
    }
    setWebsites(websites) {
        this._features.multiWebsite = websites.length > 1;
        this._websites = websites;
    }
    setFrontAvailableTranslations(languages) {
        this._features.frontTranslation = languages.length > 1;
        this._frontLanguages = languages;
    }
    setEditingAvailableTranslations(languages) {
        this._features.editingTranslation = languages.length > 1;
        this._editingLanguages = languages;
    }
    setSmartFormStatus(status) {
        this._features.smartForm = status;
    }
    on(e, callback) {
        return this.eventsManager.addListener(e, callback);
    }
    renderDOM({ wrapper: Wrapper = undefined, rootElementId = 'root', } = {
        rootElementId: 'root',
    }) {
        return __awaiter(this, void 0, void 0, function* () {
            if (window['smartfire'] && window['smartfire'].components) {
                const promisedComponents = Object.values(window['smartfire'].components).map((block) => block());
                if (promisedComponents.length > 0)
                    yield Promise.all(promisedComponents);
            }
            const module = yield import('./ui/GlueUI');
            const GlueUI = module.GlueUI;
            ReactDOM.render(Wrapper ? (React.createElement(Wrapper, null,
                React.createElement(GlueUI, null))) : (React.createElement(GlueUI, null)), document.getElementById(rootElementId));
        });
    }
    setRoutes(routes) {
        this._routes = routes;
        this.eventsManager.fire(GlueEvents.ROUTING_REFRESH, this);
        return this._routes;
    }
    addRoutes(routes) {
        return this.setRoutes(mergeGlueRouteObjectArrays(this._routes, routes));
    }
    registerSetting(setting) {
        if (this._settings.some((element) => element.key === setting.key))
            throw 'Setting key already exists';
        this._settings.push(setting);
        this.eventsManager.fire(GlueEvents.SETTING_REFRESH, this);
        return setting;
    }
    registerLayout(layout) {
        this._layouts.push(layout);
        return layout;
    }
    getLayoutFor(type) {
        return this._layouts.filter((element) => { var _a; return (_a = element.availableFor) === null || _a === void 0 ? void 0 : _a.includes(type); });
    }
    // registerBlock<
    //     SmartPropsForBlock extends SmartBlockSmartPropsDefinition = {[k: string]: SmartProps},
    //     ReactProps extends {[k: string]: any} = {[k in keyof SmartPropsForBlock]: any} & {[k: string]: any},
    //     ReactElement extends FunctionComponent<ReactProps> = FunctionComponent<ReactProps>>
    // (name: string, block: DefineBlockProp<SmartPropsForBlock, ReactProps, ReactElement>, vendor = 'smartfire') {
    //     BlockApi.registerBlock<SmartPropsForBlock, ReactProps, ReactElement>(name, () => Promise.resolve(defineBlock<SmartPropsForBlock, ReactProps, ReactElement>(block)), vendor);
    //     return block;
    // }
    registerComponent(component) {
        if (component.priority === undefined) {
            component.priority = Infinity;
        }
        this._components.push(component);
        this.eventsManager.fire(GlueEvents.COMPONENTS_REFRESH, this);
        return component;
    }
    registerBlock(name, block, vendor = 'smartfire') {
        BlockApi.registerBlock(name, () => Promise.resolve(defineBlock(block)), vendor);
        return block;
    }
    getComponent(kind) {
        const candidates = this._components
            .filter((c) => c.kind === kind)
            .sort((a, b) => getPriorityLevel(a) - getPriorityLevel(b));
        if (candidates.length < 1) {
            return undefined;
        }
        else {
            return candidates[0].component;
        }
    }
    addFilter(kind, filter, priority = 10) {
        if (!this.filters[kind]) {
            this.filters[kind] = [];
        }
        this.filters[kind].push({
            filter,
            priority,
        });
        this.eventsManager.fire(GlueEvents.ADD_FILTER_PREFIX + kind, this);
    }
    applyFilter(kind, value) {
        if (!this.filters[kind]) {
            return value;
        }
        this.filters[kind]
            .sort((a, b) => a.priority - b.priority)
            .forEach((element) => {
            value = element.filter(value);
        });
        return value;
    }
}
const glue = new GlueAPI();
GlueAPI.Context = React.createContext(glue);
export const setRoutes = glue.setRoutes.bind(glue);
export const addRoutes = glue.addRoutes.bind(glue);
export const registerLayout = glue.registerLayout.bind(glue);
export const registerComponent = glue.registerComponent.bind(glue);
export const registerBlock = glue.registerBlock.bind(glue);
export const registerSetting = glue.registerSetting.bind(glue);
export const getLayoutFor = glue.getLayoutFor.bind(glue);
export * from './hooks';
triggerGlueReady(glue);
export default glue;
//# sourceMappingURL=glue.js.map